// src/utils/authMetrics.ts
export interface AuthMetricsEvent {
  operation: string;
  duration: number;
  success: boolean;
  error?: string;
  timestamp: Date;
}

export class AuthMetrics {
  private static instance: AuthMetrics;
  private metrics: Map<string, number> = new Map();
  private errors: Error[] = [];
  private readonly MAX_ERRORS = 100;

  static getInstance(): AuthMetrics {
    if (!AuthMetrics.instance) {
      AuthMetrics.instance = new AuthMetrics();
    }
    return AuthMetrics.instance;
  }

  startOperation(operation: string): void {
    const key = `${operation}_start`;
    this.metrics.set(key, Date.now());
  }

  recordSuccess(operation: string, detail?: string): void {
    const startKey = `${operation}_start`;
    const startTime = this.metrics.get(startKey);

    if (startTime) {
      const duration = Date.now() - startTime;

      // Record to analytics
      this.recordEvent("auth_success", {
        operation,
        duration,
        detail,
      });

      // Clean up
      this.metrics.delete(startKey);
    }
  }

  recordError(operation: string, error: any): void {
    const startKey = `${operation}_start`;
    const startTime = this.metrics.get(startKey);

    const errorObj = error instanceof Error ? error : new Error(String(error));

    this.errors.push(errorObj);
    if (this.errors.length > this.MAX_ERRORS) {
      this.errors = this.errors.slice(-this.MAX_ERRORS);
    }

    this.recordEvent("auth_error", {
      operation,
      error: errorObj.message,
      stack: errorObj.stack,
      duration: startTime ? Date.now() - startTime : undefined,
    });

    // Report to error tracking service
    this.reportError(errorObj, operation);

    // Clean up
    this.metrics.delete(startKey);
  }

  recordEvent(eventName: string, properties: Record<string, any>): void {
    // Send to your analytics service
    if (typeof window !== "undefined") {
      // Google Analytics
      if (window.gtag) {
        window.gtag("event", eventName, properties);
      }

      // Segment
      if (window.analytics) {
        window.analytics.track(eventName, properties);
      }

      // Custom logging
      console.debug(`[AuthMetrics] ${eventName}:`, properties);
    }
  }

  private reportError(error: Error, context: string): void {
    // Integrate with error reporting services
    if (typeof window !== "undefined") {
      // Sentry
      if (window.Sentry) {
        window.Sentry.captureException(error, {
          tags: { context },
        });
      }

      // LogRocket
      if (window.LogRocket) {
        window.LogRocket.captureException(error);
      }
    }
  }

  getMetrics(): {
    operations: Map<string, number>;
    errorCount: number;
    recentErrors: Error[];
  } {
    return {
      operations: new Map(this.metrics),
      errorCount: this.errors.length,
      recentErrors: this.errors.slice(-10),
    };
  }

  clearMetrics(): void {
    this.metrics.clear();
    this.errors = [];
  }
}

// Export individual methods for easier use
export const startOperation = (operation: string) =>
  AuthMetrics.getInstance().startOperation(operation);

export const recordSuccess = (operation: string, detail?: string) =>
  AuthMetrics.getInstance().recordSuccess(operation, detail);

export const recordError = (operation: string, error: any) =>
  AuthMetrics.getInstance().recordError(operation, error);

export const recordEvent = (
  eventName: string,
  properties: Record<string, any>,
) => AuthMetrics.getInstance().recordEvent(eventName, properties);
